import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Crown, Feather, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Loader } from "@/components/Loader";
import { GoalRing } from "@/components/GoalRing";
import { StreakBadge } from "@/components/StreakBadge";
import { AppearanceLink } from "@/components/AppearanceLink";
import { WorkForm, emptyWork, workBody } from "@/components/WorkForm";
import { api, formatApiError } from "@/lib/api";
import { WorkCard } from "@/components/WorkCard";
import { keepOpenForChips } from "@/components/ChipInput";
import { WorkFilters } from "@/components/WorkFilters";
import { CollectionDialog, CollectionsBar } from "@/components/CollectionsBar";
import { inCollection, localDay, matches, searchHit, sortWorks } from "@/lib/writing";

const Upgrade = ({ open, onOpenChange, limit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-panel border-line" data-testid="work-limit-dialog">
      <Crown className="w-8 h-8 text-amber-400" />
      <DialogTitle className="font-display text-3xl text-amber-50">You've used your {limit} free works</DialogTitle>
      <DialogDescription className="text-slate-400">Lorely Pro gives you unlimited works and worlds. Your current works stay right where they are either way.</DialogDescription>
      <div className="flex gap-3 pt-2">
        <Link to="/billing" className="rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent font-semibold px-6 py-2.5 text-sm" data-testid="work-limit-upgrade-btn">See Pro plans</Link>
        <button onClick={() => onOpenChange(false)} className="text-sm text-slate-400 px-4">Not now</button>
      </div>
    </DialogContent>
  </Dialog>
);

const NewWork = ({ open, onOpenChange, onLimit }) => {
  const nav = useNavigate();
  const [f, setF] = useState(emptyWork);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/writing/works", workBody(f)); nav(`/writing/${data.id}`); }
    catch (err) { if (err.response?.status === 402) { onOpenChange(false); onLimit(); } else toast.error(formatApiError(err)); setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (o) setF(emptyWork); }}>
      <DialogContent onEscapeKeyDown={keepOpenForChips} className="bg-panel border-line max-h-[90vh] overflow-y-auto" data-testid="new-work-dialog">
        <DialogTitle className="font-display text-3xl text-amber-50">New work</DialogTitle>
        <DialogDescription className="text-slate-400 text-sm">You can change any of this later.</DialogDescription>
        <form onSubmit={submit} className="space-y-5">
          <WorkForm value={f} onChange={setF} />
          <button disabled={busy} className="w-full rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent font-semibold h-11" data-testid="new-work-create-btn">{busy ? "Creating…" : "Create"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const useCollections = () => {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/writing/collections").then((r) => setList(r.data)); }, []);
  const upsert = (c) => setList((l) => (l.some((x) => x.id === c.id) ? l.map((x) => (x.id === c.id ? c : x)) : [...l, c]));
  return { list, upsert, drop: (id) => setList((l) => l.filter((x) => x.id !== id)) };
};

export default function WritingDesk() {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState({ today: 0, goal: 500 });
  const [open, setOpen] = useState(false);
  const [limitOpen, setLimitOpen] = useState(false);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState("edited");
  const [current, setCurrent] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [editingFilters, setEditingFilters] = useState(null);
  const cols = useCollections();
  useEffect(() => {
    api.get("/writing/works").then((r) => setData(r.data));
    api.get("/writing/stats", { params: { day: localDay() } }).then((r) => setStats(r.data));
  }, []);
  const all = useMemo(() => data?.works || [], [data]);
  const col = cols.list.find((c) => c.id === current);
  const counts = Object.fromEntries(cols.list.map((c) => [c.id, all.filter((w) => inCollection(w, c)).length]));
  const works = useMemo(() => sortWorks(all.filter((w) => (!col || inCollection(w, col)) && matches(w, filters) && searchHit(w, q)), sort), [all, col, filters, q, sort]);
  const atLimit = data?.limit != null && all.length >= data.limit;
  const start = () => (atLimit ? setLimitOpen(true) : setOpen(true));
  const pickCollection = (id, obj) => {
    setCurrent(id); setFilters({}); setEditingFilters(null);
    const c = obj || cols.list.find((x) => x.id === id);
    setSort(c?.kind === "filter" ? c.sort : "edited");
  };
  const toggleMember = async (c, w, add) => {
    try { const { data: d } = await api.post(`/writing/collections/${c.id}/works`, { work_id: w.id, add }); cols.upsert(d); toast.success(add ? `Added to ${c.name}` : `Removed from ${c.name}`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const saveFilters = async () => {
    const c = editingFilters;
    try { const { data: d } = await api.put(`/writing/collections/${c.id}`, { name: c.name, description: c.description, kind: c.kind, filters, sort }); cols.upsert(d); setEditingFilters(null); setFilters({}); setCurrent(d.id); toast.success("Collection filters saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="relative z-10 min-h-screen" data-testid="writing-desk-page">
      <header className="flex items-center justify-between gap-2 px-4 sm:px-6 md:px-12 py-5 border-b border-line">
        <Link to="/worlds" className="flex items-center gap-2 text-sm text-slate-400 hover:text-amber-300" data-testid="writing-back-btn"><ArrowLeft className="w-4 h-4" /><span className="hidden sm:inline">Library</span></Link>
        <span className="font-display text-2xl text-amber-100 whitespace-nowrap">Lore<span className="text-amber-500">ly</span><span className="hidden sm:inline text-slate-500 text-lg"> Writing</span></span>
        <div className="flex items-center gap-1 sm:gap-2"><AppearanceLink /><StreakBadge stats={stats} /><GoalRing today={stats.today} goal={stats.goal} onGoal={(goal) => setStats({ ...stats, goal })} compact /></div>
      </header>
      <main className="px-6 md:px-12 py-12 max-w-7xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <p className="font-code text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] text-amber-500/80 mb-3">Writing desk{data?.limit != null ? ` · ${all.length}/${data.limit} on Free` : ""}</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-amber-50">{col ? col.name : "Your works"}</h1>
            {col?.description && <p className="text-slate-400 mt-2 max-w-xl" data-testid="collection-description">{col.description}</p>}
          </div>
          <button onClick={start} className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-onaccent font-semibold hover:bg-amber-400 transition-colors" data-testid="new-work-btn"><Plus className="w-4 h-4" /> New work</button>
        </div>
        {data === null ? <Loader /> : all.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-line p-16 text-center" data-testid="works-empty">
            <Feather className="w-8 h-8 text-amber-400 mx-auto" />
            <p className="font-display text-3xl text-amber-100 mt-4">The page is waiting.</p>
            <p className="text-slate-400 mt-2">Stories, fanfics and drabbles live here, chapter by chapter.</p>
            <button onClick={start} className="mt-6 rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent font-semibold px-6 py-2.5" data-testid="start-first-work-btn">Start your first work</button>
          </div>
        ) : (
          <>
            <CollectionsBar collections={cols.list} counts={counts} total={all.length} current={current} setCurrent={pickCollection}
              onNew={() => setDialog({ kind: "manual" })} onEdit={(c) => setDialog({ collection: c })} onDeleted={(id) => { cols.drop(id); pickCollection(null); }}
              onEditFilters={(c) => { setCurrent(null); setFilters(c.filters || {}); setSort(c.sort || "edited"); setEditingFilters(c); }} />
            {editingFilters && (
              <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-100" data-testid="editing-filters-banner">
                Editing the filters for <strong>{editingFilters.name}</strong>. Change them below, then save.
                <button onClick={saveFilters} className="ml-auto rounded-full bg-amber-500 text-onaccent font-semibold text-xs px-4 py-1.5" data-testid="editing-filters-save">Save filters</button>
                <button onClick={() => pickCollection(editingFilters.id, editingFilters)} className="text-xs text-slate-400" data-testid="editing-filters-cancel">Cancel</button>
              </div>
            )}
            <WorkFilters works={all} q={q} setQ={setQ} filters={filters} setFilters={setFilters} sort={sort} setSort={setSort}
              canSave={!editingFilters} onSave={() => setDialog({ kind: "filter" })} />
            {works.length === 0 ? <p className="text-slate-500 text-sm" data-testid="works-no-match">{col && !Object.keys(filters).length && !q ? "This collection is empty." : "No works match."}</p> : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="works-grid">
                {works.map((w, i) => <WorkCard key={w.id} w={w} i={i} collections={cols.list} onToggle={toggleMember} />)}
              </div>
            )}
          </>
        )}
      </main>
      <NewWork open={open} onOpenChange={setOpen} onLimit={() => setLimitOpen(true)} />
      <Upgrade open={limitOpen} onOpenChange={setLimitOpen} limit={data?.limit} />
      <CollectionDialog state={dialog} onClose={() => setDialog(null)} filters={filters} sort={sort}
        onSaved={(c, created) => { cols.upsert(c); if (created) { pickCollection(c.id, c); setQ(""); } }} />
    </div>
  );
}
